/*
 * @author bai <ghimping@alpstein.my>
 * @link https://www.alpstein.my
 * @copyright Copyright (c) Alpstein Software
 *            _____                    _____                    _____
 *           /\    \                  /\    \                  /\    \
 *          /::\    \                /::\    \                /::\    \
 *         /::::\    \              /::::\    \               \:::\    \
 *        /::::::\    \            /::::::\    \               \:::\    \
 *       /:::/\:::\    \          /:::/\:::\    \               \:::\    \
 *      /:::/__\:::\    \        /:::/__\:::\    \               \:::\    \
 *     /::::\   \:::\    \      /::::\   \:::\    \              /::::\    \
 *    /::::::\   \:::\    \    /::::::\   \:::\    \    ____    /::::::\    \
 *   /:::/\:::\   \:::\ ___\  /:::/\:::\   \:::\    \  /\   \  /:::/\:::\    \
 *  /:::/__\:::\   \:::|    |/:::/  \:::\   \:::\____\/::\   \/:::/  \:::\____\
 *  \:::\   \:::\  /:::|____|\::/    \:::\  /:::/    /\:::\  /:::/    \::/    /
 *   \:::\   \:::\/:::/    /  \/____/ \:::\/:::/    /  \:::\/:::/    / \/____/
 *    \:::\   \::::::/    /            \::::::/    /    \::::::/    /
 *     \:::\   \::::/    /              \::::/    /      \::::/____/
 *      \:::\  /:::/    /               /:::/    /        \:::\    \
 *       \:::\/:::/    /               /:::/    /          \:::\    \
 *        \::::::/    /               /:::/    /            \:::\    \
 *         \::::/    /               /:::/    /              \:::\____\
 *          \::/____/                \::/    /                \::/    /
 *           ~~                       \/____/                  \/____/
 *
 */

import React from "react";
import { call, put } from "redux-saga/effects";
import { push } from "connected-react-router";
import { intl } from "../services/intlGlobalProvider";
import * as meActions from "../actions/me";
import * as authActions from "../actions/auth";
import * as userActions from "../actions/user";
import * as profileActions from "../actions/profile";
import * as favouriteActions from "../actions/favourite";
import api from "../services/httpUtilities/httpService";
import httpErrorHelpers from "../services/httpUtilities/httpErrorHelpers";
import AuthManager from "../utils/AuthManager";
import Toast from "../utils/Toast";

function* watcherLoginWithFacebookRequest({ fbAccessToken }) {
  try {
    const response = yield call(api.loginWithFacebook, fbAccessToken);

    const { token, success, messages } = response.data;

    if (success) {
      Toast.success(messages ? messages[0].text : "");
    }

    yield put(authActions.updateAccessToken(token));

    yield put(authActions.loginWithFacebookSuccess(token));

    yield call(navigateToApp);
  } catch (error) {
    yield call(httpErrorHelpers, error, authActions.loginWithFacebookFailure);
  }
}

function* watcherSignUpAccountRequest({ data }) {
  try {
    const response = yield call(api.signUpAccount, data);

    const { token, success } = response.data;

    if (success) {
      Toast.success(
        intl.formatMessage({
          id: "auth.register.successful",
          defaultMessage: "Sign Up Successfully",
          description: "Sign Up Successful Title"
        })
      );
    }

    yield put(authActions.updateAccessToken(token));

    yield put(authActions.signUpAccountSuccess(token));

    yield call(navigateToApp);
  } catch (error) {
    yield call(httpErrorHelpers, error, authActions.signUpAccountFailure);
  }
}

function* watcherLoginRequest({ mobileNumber, password, rememberMe }) {
  try {
    const response = yield call(api.loginAccount, mobileNumber, password);

    const { token, success, messages } = response.data;

    if (success) {
      Toast.success(messages ? messages[0].text : "");

      if (rememberMe) {
        yield put(authActions.updateUserName(mobileNumber));
      } else {
        yield put(authActions.clearUserName());
      }

      yield put(authActions.updateAccessToken(token));

      yield put(authActions.loginAccountSuccess(token));

      yield call(navigateToApp);
    }
  } catch (error) {
    yield call(httpErrorHelpers, error, authActions.loginAccountFailure);
  }
}

function* watcherLogoutAccount(payload) {
  yield put(authActions.clearAccessToken());
  yield put(userActions.clearOrderIndicator());

  yield put(authActions.logoutAccountSuccess());
  yield put(userActions.resetCartState());
  yield put(favouriteActions.resetFavouriteFoodListState());
}

function* watcherPostForgetPassword({ phoneData }) {
  try {
    const response = yield call(api.requestForgetPassword, phoneData);
    const { success, item } = response.data;

    if (success) {
      yield put(authActions.postForgetPasswordSuccess(item));
    }
  } catch (error) {
    yield call(httpErrorHelpers, error, authActions.postForgetPasswordFailure);
  }
}
function* watcherPostResetPasswordWithPhone({ resetData }) {
  try {
    const response = yield call(api.requestResetPassword, resetData);
    const { success, item } = response.data;

    if (success) {
      yield put(authActions.postResetPasswordWithPhoneSuccess(item));
    }
  } catch (error) {
    yield call(
      httpErrorHelpers,
      error,
      authActions.postResetPasswordWithPhoneFailure
    );
  }
}

function* watcherPostResetPasswordWithEmail({ resetData }) {
  try {
    const response = yield call(api.requestResetPassword, resetData);
    const { success, messages } = response.data;

    if (success) {
      yield put(authActions.postResetPasswordWithEmailSuccess());
      Toast.success(messages ? messages[0].text : "");
    }
  } catch (error) {
    yield call(
      httpErrorHelpers,
      error,
      authActions.postResetPasswordWithEmailFailure
    );
  }
}

function* navigateToApp() {
  yield put(push("/shop/stall"));
}

function* watcherUpdateUserName({ mobileNumber }) {
  AuthManager.setUserName(mobileNumber);
}

function* watcherClearUserName() {
  AuthManager.removeUserName();
}

function* watcherSetProfileStatus() {
  AuthManager.setProfileStatus();
  yield put(
    authActions.setLoginProfileStatus(AuthManager.retrieveProfileStatus())
  );
}

function* watcherUpdateProfileStatus() {
  AuthManager.updateProfileStatus();
  yield put(authActions.setLoginProfileStatus("false"));
}

function* watcherClearProfileStatus() {
  AuthManager.removeProfileStatus();
  yield put(authActions.setLoginProfileStatus("false"));
  yield put(profileActions.getProfileBasicFailure());
}

function* watcherUpdateAccessToken({ token }) {
  AuthManager.setToken(token);

  api.setHeaderToken(token);
}

function* watcherClearAccessToken() {
  AuthManager.removeToken();

  api.setHeaderToken(null);
}

function* watcherLinkSocialNetworkRequest(payload) {
  try {
    const channel = payload.channel;
    const fbAccessToken = payload.token;
    const response = yield call(api.linkSocial, channel, fbAccessToken);

    const { success, messages } = response.data;

    if (success) {
      Toast.success(messages ? messages[0].text : "");
    }
    yield put(meActions.getMeDataRequest());
  } catch (error) {
    yield call(httpErrorHelpers, error, authActions.linkSocialNetworkFailure);
  }
}

export {
  watcherLoginWithFacebookRequest,
  watcherSignUpAccountRequest,
  watcherLoginRequest,
  watcherLogoutAccount,
  watcherUpdateAccessToken,
  watcherClearAccessToken,
  watcherUpdateUserName,
  watcherClearUserName,
  watcherPostForgetPassword,
  watcherPostResetPasswordWithPhone,
  watcherPostResetPasswordWithEmail,
  watcherClearProfileStatus,
  watcherUpdateProfileStatus,
  watcherSetProfileStatus,
  watcherLinkSocialNetworkRequest
};



// WEBPACK FOOTER //
// ./src/sagas/auth.js